import React, { useState } from "react";
import PencilLineArt from "../../../assets/icons/PencilLineArt";
import LinkLineArt from "../../../assets/icons/LinkLineArt";
import MicLineArt from "../../../assets/icons/MicLineArt";
import CaptureFrame from "../../../assets/icons/CaptureFrame";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
// QA-93 (Gordon): microphone glyph for audio captures in the list thumb.
// Line-art link glyph — the product's voice. Never an emoji.
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
// Line-art download glyph — the product's voice. Never an emoji.
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { colors } from "../../../utils";
import { fileIcons } from "../../../constants/documentIcon";
import { ASSETS } from "../../../utils/config";
import { captureDocumentType } from "../captureDocumentType";
import {
  VIDEO_POSTER_PRELOAD,
  withVideoPosterFrame,
} from "../../../utils/videoPosterFrame";
import {
  captureSourceUrl,
  linkFaviconUrl,
  linkDisplayLabel,
} from "../captureLinks";
import { audioPlaybackNotice } from "../../../utils/audioFormats";
import { CAPTURE_TYPES } from "../Capture.helpers";
import {
  Thumb,
  CaptureLinkAnchor,
  DocLinkAnchor,
  ProvenanceAnchor,
} from "../styles";
import { CardNotice } from "./CapturedOnWheel.styles";

/**
 * Shared capture preview — a single source of truth for how a capture item's
 * TYPE is rendered, used by both the inbox card (`CaptureCard`) and the
 * wheel-side read-back drawer (`CapturedOnWheel`), which previously each carried
 * a byte-identical private `Preview` copy (§ shared-component law). Extending it
 * to video/audio in one place keeps the two surfaces from drifting.
 *
 * All media plays from the backend's durable `item.media_url` (an https S3 URL
 * set server-side on create) — never a `blob:`/local object URL, so it is safe
 * by construction under the #2176 "never persist/play a blob src" rule.
 */

/**
 * The video tile's thumbnail, in preference order (each a strict fallback of
 * the one above — additive, no capability removed):
 *
 *   1. QA-114: the backend's SERVER-RENDERED poster (`item.poster_url`, Backend
 *      #2247, a derived `<media_key>.poster.jpg`). A single cheap <img> GET off
 *      the CDN, so the viewer's browser never fetches the video just to paint a
 *      still. Preferred whenever the backend supplies one.
 *   2. QA-83: the CLIENT-SIDE frame — `<video>` with `#t=0.1` + preload=metadata
 *      (utils/videoPosterFrame). Kept as the fallback for a capture whose server
 *      poster is missing or 404s, and for older rows the backend never
 *      back-filled. `controls` is deliberately absent (this is the 56px thumb,
 *      not the player — CaptureMediaPlayer is the player); `muted`+`playsInline`
 *      keep iOS Safari from promoting it to a fullscreen player on tap.
 *   3. The generic line-art movie glyph, when there is no media at all.
 *
 * Own component (not an inline branch) because the poster→client-frame fallback
 * is stateful: a 404 on the server poster must swap to the client frame, which
 * needs `useState`, and a hook cannot live inside CapturePreviewThumb's
 * conditional early returns.
 */
const THUMB_GLYPH_STYLE = { color: colors.PrimaryPurple600 };

const VideoCaptureThumb = ({ item }) => {
  const [posterFailed, setPosterFailed] = useState(false);
  const serverPoster = item.poster_url;

  if (serverPoster && !posterFailed) {
    return (
      <Thumb>
        <img
          className="video-poster-img"
          data-testid="capture-video-poster-image"
          src={serverPoster}
          alt={item.text_content || "Captured video"}
          // A 404 / broken poster object drops us to the client-side frame
          // rather than showing a broken-image icon.
          onError={() => setPosterFailed(true)}
        />
      </Thumb>
    );
  }

  if (item.media_url) {
    return (
      <Thumb>
        <video
          className="video-poster"
          src={withVideoPosterFrame(item.media_url)}
          preload={VIDEO_POSTER_PRELOAD}
          muted
          playsInline
          tabIndex={-1}
          aria-hidden="true"
          data-testid="capture-video-poster"
        />
      </Thumb>
    );
  }

  return (
    <Thumb>
      <MovieOutlinedIcon style={THUMB_GLYPH_STYLE} />
    </Thumb>
  );
};

/** Link thumb — site favicon when available; Lucide link glyph as fallback. */
const LinkCaptureThumb = ({ item }) => {
  const [faviconFailed, setFaviconFailed] = useState(false);
  const favicon = linkFaviconUrl(captureSourceUrl(item));
  const showFavicon = Boolean(favicon) && !faviconFailed;

  return (
    <Thumb>
      {showFavicon ? (
        <img
          className="link-favicon"
          data-testid="capture-link-favicon"
          src={favicon}
          alt=""
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <LinkLineArt
          data-testid="capture-link-glyph"
          color={THUMB_GLYPH_STYLE.color}
        />
      )}
    </Thumb>
  );
};

/**
 * kelly-capture-photo-thumbnail-blank: a photo capture whose `media_url`
 * 404s transiently right after upload (before the object is readable off
 * S3/CDN) rendered a bare `<img>` with no `onError` handler — the browser
 * marks it broken once and nothing ever repaints it, so the tile is stuck
 * on the pale `Thumb` background forever even after the URL resolves.
 * Bounded retry-with-backoff (mirrors the fix already shipped for this
 * exact failure mode on RN, PR#347) self-heals the common transient case.
 *
 * gordon-0901-capture-panel-cluster #2/#3 (ISP-9999): two more bugs in the
 * give-up/retry path, fixed together since they're the same few lines —
 *   #2. The give-up state rendered a generic MUI photo icon
 *       (`ImageOutlinedIcon`), not our brand. Now it's `CaptureFrame` — the
 *       SAME viewfinder-frame-plus-"+" glyph shown in the header nav's
 *       Capture entry (`CaptureNavEntry.jsx`).
 *   #3. That placeholder only appeared once retries were exhausted, so every
 *       failed/retrying frame in between showed the browser's OWN
 *       broken-image glyph (alt text spilling out of the tile) first. Now the
 *       `<img>` stays mounted (so it keeps trying/retrying) but is visually
 *       HIDDEN until it actually decodes (`onLoad`), and `CaptureFrame`
 *       covers the gap in front of it the whole time — first paint, every
 *       retry, and permanent failure alike. The browser's broken-image glyph
 *       is never painted because the broken `<img>` is never visible.
 */
const PHOTO_RETRY_DELAYS_MS = [1500, 3000, 4500];

const PhotoCaptureThumb = ({ item }) => {
  const [attempt, setAttempt] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleError = () => {
    setLoaded(false);
    if (attempt >= PHOTO_RETRY_DELAYS_MS.length) {
      setExhausted(true);
      return;
    }
    timerRef.current = setTimeout(
      () => setAttempt((a) => a + 1),
      PHOTO_RETRY_DELAYS_MS[attempt]
    );
  };

  return (
    <Thumb>
      {!loaded && (
        <CaptureFrame
          data-testid="capture-photo-placeholder"
          color={THUMB_GLYPH_STYLE.color}
          width={24}
          height={24}
        />
      )}
      {!exhausted && (
        <img
          key={attempt}
          src={item.media_url}
          alt={item.text_content || "Captured photo"}
          onLoad={() => setLoaded(true)}
          onError={handleError}
          style={{ display: loaded ? "block" : "none" }}
        />
      )}
    </Thumb>
  );
};

const DocumentCaptureThumb = ({ item }) => {
  const [iconError, setIconError] = useState(false);
  const docType = captureDocumentType(item);
  const iconSrc = (!iconError && fileIcons[docType]) || ASSETS.doc;

  return (
    <Thumb>
      <img
        className="doc-icon"
        data-testid="capture-doc-icon"
        src={iconSrc}
        alt="Document"
        onError={() => setIconError(true)}
      />
    </Thumb>
  );
};

/**
 * ISP-9999 (capture-medialib-delete-sync-orphan, Gordon): a photo whose
 * backing file the BACKEND has already confirmed gone (``media_missing``,
 * set once ``media_missing_at`` is stamped -- see the Backend serializer)
 * must never even attempt an `<img>` load. `PhotoCaptureThumb`'s retry dance
 * below is for a TRANSIENT failure (upload/CDN propagation lag) and always
 * starts its state fresh on every mount, so a PERMANENTLY dead file (like
 * Gordon's reported IMG_8067.jpeg) flashes the browser's native broken-image
 * icon on every single page load while it re-runs the full ~9s retry cycle,
 * every time, forever. Once the backend already knows the answer there is
 * nothing to retry -- show the stable "file missing" glyph immediately.
 */
const MissingPhotoThumb = ({ item }) => (
  <Thumb>
    <ImageOutlinedIcon
      data-testid="capture-photo-missing-glyph"
      style={THUMB_GLYPH_STYLE}
      titleAccess={item.title || "Original file no longer available"}
    />
  </Thumb>
);

/** Small square thumbnail glyph for the item's type (line-art, no emoji). */
export const CapturePreviewThumb = ({ item }) => {
  if (item.type === CAPTURE_TYPES.PHOTO && item.media_missing) {
    return <MissingPhotoThumb item={item} />;
  }
  if (item.type === CAPTURE_TYPES.PHOTO && item.media_url) {
    return <PhotoCaptureThumb item={item} />;
  }
  if (item.type === CAPTURE_TYPES.VIDEO) {
    return <VideoCaptureThumb item={item} />;
  }
  if (item.type === CAPTURE_TYPES.AUDIO) {
    return (
      <Thumb>
        <MicLineArt
          data-testid="capture-audio-glyph"
          color={THUMB_GLYPH_STYLE.color}
        />
      </Thumb>
    );
  }
  if (item.type === CAPTURE_TYPES.LINK) {
    return <LinkCaptureThumb item={item} />;
  }
  if (item.type === CAPTURE_TYPES.DOC) {
    return <DocumentCaptureThumb item={item} />;
  }
  return (
    <Thumb>
      <PencilLineArt
        data-testid="capture-note-glyph"
        color={THUMB_GLYPH_STYLE.color}
      />
    </Thumb>
  );
};

/**
 * The playable media element for a video/audio capture, rendered in the card
 * body (mirrors the DOC "Open document" affordance's placement). Returns null
 * for non-media types or when the backend has no resolved `media_url`. `src`
 * is always the durable https URL — never a blob (#2176-safe).
 */
export const CaptureMediaPlayer = ({ item }) => {
  if (item.type === CAPTURE_TYPES.LINK) {
    /**
     * QA-98 — a link capture's payload IS the URL, so the card shows it and
     * makes it openable. `rel="noreferrer"` covers noopener too: without it the
     * opened page gets a handle on this window via `window.opener` (a reverse-
     * tabnabbing hole), and it also stops the destination learning which iYuga
     * page the user came from.
     */
    const url = captureSourceUrl(item);
    if (!url) return null;
    return (
      <CaptureLinkAnchor
        href={url}
        target="_blank"
        rel="noreferrer"
        data-testid="capture-link-open"
      >
        <OpenInNewRoundedIcon style={{ fontSize: 14 }} aria-hidden="true" />
        {linkDisplayLabel(url) || url}
      </CaptureLinkAnchor>
    );
  }
  if (!item.media_url) {
    return null;
  }
  if (item.type === CAPTURE_TYPES.VIDEO) {
    return (
      <video
        src={item.media_url}
        controls
        playsInline
        preload={VIDEO_POSTER_PRELOAD}
        aria-label={item.text_content || "Captured video"}
        data-testid="capture-video-player"
        style={{
          width: "100%",
          maxHeight: 220,
          borderRadius: 8,
          backgroundColor: colors.black,
          display: "block",
        }}
      />
    );
  }
  if (item.type === CAPTURE_TYPES.AUDIO) {
    /**
     * QA-97 — some accepted audio formats cannot be decoded by ANY browser
     * (Audible's DRM-protected .aa/.aax; .wma has no decoder). Rendering a
     * player for one produces a control that never starts, which reads as "the
     * upload failed" — the silence-is-broken failure this codebase has paid for
     * repeatedly. So the file is confirmed SAVED and the limit is stated, with a
     * real download link, instead of a dead player.
     */
    const notice = audioPlaybackNotice(item.file_name || item.media_key || item.media_url);
    if (notice) {
      return (
        <div data-testid="capture-audio-unplayable">
          <CardNotice role="status">{notice}</CardNotice>
          <DocLinkAnchor
            href={item.media_url}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="capture-audio-download"
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 14 }} aria-hidden="true" />
            Download audio
          </DocLinkAnchor>
        </div>
      );
    }
    return (
      <audio
        src={item.media_url}
        controls
        preload="metadata"
        aria-label={item.text_content || "Captured audio"}
        data-testid="capture-audio-player"
        style={{ width: "100%", display: "block" }}
      />
    );
  }
  return null;
};

/**
 * QA-99 — where an UPLOADED capture came from.
 *
 * Gordon: "If Capture media is sourced from an Embed URL or a Shared URL from a
 * 3rd party (e.g. YouTube), we need to SAVE the source URL on the capture
 * item." Saving it is the backend's half; this is the half that makes it worth
 * saving — an embedded video that cannot say where it came from has provenance
 * only a database query can see, which is the same as none.
 *
 * Renders nothing for a LINK capture: its URL is already the payload above, and
 * repeating it as "From …" underneath is noise.
 */
export const CaptureProvenance = ({ item }) => {
  if (!item || item.type === CAPTURE_TYPES.LINK) return null;
  const url = captureSourceUrl(item);
  if (!url) return null;
  return (
    <ProvenanceAnchor
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid="capture-provenance"
    >
      <LinkRoundedIcon style={{ fontSize: 12 }} aria-hidden="true" />
      From {linkDisplayLabel(url) || url}
    </ProvenanceAnchor>
  );
};
